/**
 * CollabJoinPage — end-user Jitsi meeting join page.
 *
 * This page is the join-only collab surface for AquaStar end-users.
 * It is NOT in the nav bar — only reachable via:
 *   1. A notification tap (meeting_invite) → /collab/:roomId
 *   2. A direct deep link from an admin invitation
 *
 * The page shows:
 *   - Room name
 *   - "Meeting in progress" indicator (polls getActiveSession every 10s)
 *   - "Join meeting" button — enabled when a session is active
 *   - Graceful 403 / 409 handling
 *
 * On join: calls POST /v1/collab/rooms/:roomId/sessions/join → receives a
 * personal Jitsi JWT → opens jitsiUrl in a new tab. The page itself stays
 * open so the user can re-join if needed.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { ApiError } from '../lib/apiClient';
import { getActiveSession, joinSession, markInviteRead } from '../lib/collabApi';
import type { ActiveSession } from '../lib/collabApi';
import { useAuth } from '../lib/auth';

const POLL_INTERVAL_MS = 10_000;

export function CollabJoinPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const { user } = useAuth();

  // The room name comes from the invite record (passed via state) or falls back
  // to a generic label once we confirm membership via the active-session check.
  const [roomName, setRoomName] = useState<string>('Meeting room');
  const [activeSession, setActiveSession] = useState<ActiveSession | null | 'loading'>('loading');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [joined, setJoined] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inviteIdRef = useRef<string | null>(null);

  // Read invite state from navigation (set by NotificationsBell on tap)
  useEffect(() => {
    const state = (window.history.state as { usr?: { inviteId?: string; roomName?: string } } | null)?.usr;
    if (state?.roomName) setRoomName(state.roomName);
    if (state?.inviteId) inviteIdRef.current = state.inviteId;
  }, []);

  // Check for active session + start polling
  const checkSession = useCallback(async () => {
    if (!roomId) return;
    try {
      const session = await getActiveSession(roomId);
      setActiveSession(session);
      setForbidden(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setForbidden(true);
        setActiveSession(null);
        // Stop polling — user isn't a member
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
      // For other errors, leave state as-is and retry on next poll
    }
  }, [roomId]);

  useEffect(() => {
    void checkSession();
    pollRef.current = setInterval(() => {
      void checkSession();
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [checkSession]);

  // Mark the invite as read when the page loads
  useEffect(() => {
    const inviteId = inviteIdRef.current;
    if (inviteId) {
      markInviteRead(inviteId).catch(() => {});
    }
  }, []);

  async function handleJoin() {
    if (!roomId) return;
    setJoinError(null);
    setJoining(true);
    try {
      const displayName = user?.displayName || user?.givenName || user?.email;
      const token = await joinSession(roomId, displayName);
      window.open(token.jitsiUrl, '_blank', 'noopener,noreferrer');
      setJoined(true);
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 409) {
          setJoinError('The meeting has ended. You will be notified when a new one starts.');
          setActiveSession(null);
        } else if (e.status === 403) {
          setJoinError("You don't have access to this meeting room.");
          setForbidden(true);
        } else {
          setJoinError(e.message || 'Could not join the meeting. Please try again.');
        }
      } else {
        setJoinError('Could not join the meeting. Please try again.');
      }
    } finally {
      setJoining(false);
    }
  }

  const sessionActive = activeSession !== 'loading' && activeSession !== null;
  const sessionLoading = activeSession === 'loading';

  return (
    <div className="min-vh-100" style={{ background: 'var(--aqua-bg)' }}>
      <AppHeader />

      <main className="container py-5" style={{ maxWidth: 520 }}>
        {/* Back link */}
        <Link
          to="/notifications"
          className="d-inline-flex align-items-center gap-1 text-decoration-none small mb-4"
          style={{ color: 'var(--aqua-text-muted)' }}
        >
          ← Back to notifications
        </Link>

        <div
          className="card shadow-sm"
          style={{
            background: 'var(--aqua-surface)',
            border: '1px solid var(--aqua-border)',
            borderRadius: 12,
          }}
        >
          <div className="card-body p-4">
            {/* Room title */}
            <div className="d-flex align-items-center gap-3 mb-4">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                style={{
                  width: 48, height: 48,
                  background: 'var(--aqua-primary)',
                  fontSize: '1.5rem',
                }}
              >
                🎥
              </div>
              <div>
                <h4 className="mb-0 fw-bold" style={{ color: 'var(--aqua-text-strong)' }}>
                  {roomName}
                </h4>
                <span className="badge bg-info text-dark small">Meeting room</span>
              </div>
            </div>

            {/* Forbidden */}
            {forbidden && (
              <div className="alert alert-warning py-3" style={{ borderRadius: 8 }}>
                <strong>Access denied.</strong> You don't have access to this meeting room.
                If you believe this is an error, contact your administrator.
              </div>
            )}

            {/* Session status */}
            {!forbidden && (
              <>
                <div
                  className={`d-flex align-items-center gap-2 rounded-3 p-3 mb-4 ${sessionLoading ? 'bg-light' : sessionActive ? '' : 'bg-light'}`}
                  style={{
                    background: sessionActive
                      ? 'rgba(var(--bs-success-rgb), 0.1)'
                      : sessionLoading ? undefined : 'var(--aqua-surface-muted)',
                    border: `1px solid ${sessionActive ? 'rgba(var(--bs-success-rgb), 0.3)' : 'var(--aqua-border)'}`,
                  }}
                >
                  {sessionLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm text-muted" />
                      <span className="text-muted small">Checking meeting status…</span>
                    </>
                  ) : sessionActive ? (
                    <>
                      <span style={{ fontSize: '1.2rem' }}>🟢</span>
                      <div>
                        <div className="fw-semibold" style={{ color: 'var(--aqua-text-strong)', fontSize: '0.9rem' }}>
                          Meeting in progress
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          Started {new Date(activeSession!.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '1.2rem' }}>⚪</span>
                      <div>
                        <div className="fw-semibold" style={{ color: 'var(--aqua-text)', fontSize: '0.9rem' }}>
                          No meeting in progress
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          You'll be notified when one starts.
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Join error */}
                {joinError && (
                  <div className="alert alert-danger py-2 mb-3 small">{joinError}</div>
                )}

                {/* Joined confirmation */}
                {joined && !joinError && (
                  <div className="alert alert-success py-2 mb-3 small">
                    Jitsi opened in a new tab. You can close or minimize this page.
                  </div>
                )}

                {/* Join button */}
                <button
                  className="btn btn-primary w-100"
                  style={{ borderRadius: 8, fontWeight: 600, padding: '0.65rem' }}
                  disabled={!sessionActive || joining}
                  onClick={handleJoin}
                >
                  {joining ? (
                    <><span className="spinner-border spinner-border-sm me-2" />Joining…</>
                  ) : sessionActive ? (
                    '🎥 Join meeting'
                  ) : (
                    'No meeting in progress'
                  )}
                </button>

                <p className="text-muted small text-center mt-3 mb-0">
                  The meeting will open in a new tab via Jitsi Meet.
                  {!sessionActive && <> This page auto-refreshes every 10 seconds.</>}
                </p>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
